import { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';

import { MultiSelectFilter } from '@zendesk/forethought-elements';
import useDiscoverSectionViewable from 'src/hooks/discover/useDiscoverSectionViewable';
import { useGetAllTopicsQuery } from 'src/services/discover/discoverApi';
import { DateRange } from 'src/types/types';
import { dateRangeToTimeFilter } from 'src/utils/discover/helpers';

type Option = ComponentProps<typeof MultiSelectFilter>['options'][number];

export default function useGetTopicOptions({
  dateRange,
  skip,
}: {
  dateRange: DateRange;
  skip?: boolean;
}): { label: string; options: Option[]; value: string }[] {
  const { t } = useTranslation('intent-conversation-analytics');
  const { discoverAnalyticsEnabled } = useDiscoverSectionViewable();
  const timeFilter = dateRangeToTimeFilter(dateRange);
  const { data } = useGetAllTopicsQuery(
    {
      dataFilterQuery: undefined,
      timeFilter,
    },
    { skip: !discoverAnalyticsEnabled || skip },
  );

  const options: Option[] = [];

  data?.breakdown.forEach(topic => {
    const topicLabel = topic.topic?.topic_name || '';
    const hasSubtopics = topic.breakdown && topic.breakdown.length > 0;

    if (!hasSubtopics) {
      options.push({
        label: topicLabel,
        value: `topic_ids.${topic.id}`,
      });
      return;
    }

    const subtopicOptions =
      topic.breakdown?.map(subtopic => {
        const subtopicLabel = subtopic.topic?.topic_name || '';
        const hasNestedSubtopics =
          subtopic.breakdown && subtopic.breakdown.length > 0;

        if (hasNestedSubtopics) {
          return {
            label: subtopicLabel,
            options:
              subtopic.breakdown?.map(nestedSubtopic => ({
                label: nestedSubtopic.topic?.topic_name || '',
                value: `topic_ids.${nestedSubtopic.id}`, // Only deepest level is selectable
              })) || [],
            value: `parent_topic_ids.${subtopic.id}`, // Intermediate level not selectable
          };
        }

        // Leaf subtopic - directly selectable
        return {
          label: subtopicLabel,
          value: `topic_ids.${subtopic.id}`,
        };
      }) || [];

    options.push({
      label: topicLabel,
      options: subtopicOptions,
      value: `parent_topic_ids.${topic.id}`, // Parent level not selectable
    });
  });

  if (options.length === 0) {
    return [];
  }

  const topicOptions = [
    {
      label: t('topicOptions.topics'),
      options: options,
      value: 'topic_ids',
    },
  ];

  if (discoverAnalyticsEnabled && !skip) {
    return topicOptions;
  }

  return [];
}
